import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import Shell from "../Shell";
import { authHeaders } from "../api";
import { tilesForRole } from "../navConfig";

/* ───────────── Date helpers ───────────── */

const isoToDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const dateToIso = (date) => {
  if (!(date instanceof Date)) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const fmtDMY = (iso) => {
  const dt = isoToDate(iso);
  if (!dt) return "";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

function fmtDate(iso) {
  if (!iso) return "—";
  const dt = isoToDate(iso);
  if (!dt) return iso;
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/* ───────────── Mini date picker ───────────── */

function MiniDatePickerDMY({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = isoToDate(value) || new Date();
  const [view, setView] = useState(new Date(selected.getFullYear(), selected.getMonth(), 1));
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    const s = isoToDate(value);
    if (s) setView(new Date(s.getFullYear(), s.getMonth(), 1));
  }, [value]);

  const startOfMonth = new Date(view.getFullYear(), view.getMonth(), 1);
  const startDay = (startOfMonth.getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();

  const prevMonth = () => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1));
  const nextMonth = () => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1));

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(view.getFullYear(), view.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);
  while (cells.length < 42) cells.push(null);

  const isSameDay = (a, b) =>
    a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-block" }}>
      <input
        readOnly
        value={fmtDMY(value)}
        onClick={() => setOpen((v) => !v)}
        style={{ width: 160, cursor: "pointer" }}
        placeholder="dd/mm/áááá"
      />
      {open && (
        <div
          style={{
            position: "absolute",
            top: 38,
            left: 0,
            background: "#fff",
            border: "1px solid #d7dbe3",
            borderRadius: 8,
            padding: 8,
            zIndex: 50,
            boxShadow: "0 12px 28px rgba(0,0,0,.12)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 8 }}>
            <button className="btn ghost sm" type="button" onClick={prevMonth}>◀</button>
            <div style={{ fontWeight: 600 }}>
              {view.toLocaleString("is-IS", { month: "long" })} {view.getFullYear()}
            </div>
            <button className="btn ghost sm" type="button" onClick={nextMonth}>▶</button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 32px)", gap: 4, textAlign: "center", opacity: 0.7, fontSize: 12, marginBottom: 4 }}>
            {["Mán", "Þri", "Mið", "Fim", "Fös", "Lau", "Sun"].map((d) => <div key={d}>{d}</div>)}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 32px)", gap: 4, textAlign: "center" }}>
            {cells.map((c, i) => {
              const isOut = c === null || c.getMonth() !== view.getMonth();
              const active = c && isSameDay(c, selected);
              return (
                <button
                  key={i}
                  type="button"
                  disabled={!c}
                  onClick={() => {
                    if (!c) return;
                    onChange?.(dateToIso(c));
                    setOpen(false);
                  }}
                  style={{
                    width: 32,
                    height: 28,
                    borderRadius: 6,
                    border: "1px solid #d7dbe3",
                    opacity: isOut ? 0.35 : 1,
                    background: active ? "#e0ecff" : "#fff",
                    cursor: c ? "pointer" : "default",
                  }}
                >
                  {c ? c.getDate() : ""}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────────── Debounce ───────────── */

function useDebounced(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/* ───────────── Helpers ───────────── */

function matchYesNo(filterValue, actualBool) {
  if (!filterValue) return true;
  if (filterValue === "yes") return !!actualBool;
  if (filterValue === "no") return !actualBool;
  return true;
}

/* ───────────── Component ───────────── */

export default function InterviewsSummary() {
  const [role, setRole] = useState(null);
  const tiles = useMemo(() => tilesForRole(role), [role]);

  // Filters
  const todayIso = dateToIso(new Date());
  const [q, setQ] = useState("");
  const [start, setStart] = useState(todayIso);
  const [end, setEnd] = useState(todayIso);
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // yes/no filters
  const [inHouseFilter, setInHouseFilter] = useState("");
  const [onPhoneFilter, setOnPhoneFilter] = useState("");
  const [didNotArriveFilter, setDidNotArriveFilter] = useState("");
  const [flexFilter, setFlexFilter] = useState("");
  const [peerSupportFilter, setPeerSupportFilter] = useState("");
  const [rehabPlanningFilter, setRehabPlanningFilter] = useState("");
  const [rehabInterviewFilter, setRehabInterviewFilter] = useState("");
  const [urgentInterviewFilter, setUrgentInterviewFilter] = useState("");
  const [familyInterviewFilter, setFamilyInterviewFilter] = useState("");
  const [visitFilter, setVisitFilter] = useState("");
  const [handleidslaFilter, setHandleidslaFilter] = useState("");
  const [handleidslaNemarFilter, setHandleidslaNemarFilter] = useState("");
  const [ororkaFilter, setOrorkaFilter] = useState("");

  const dq = useDebounced(q, 300);

  // Data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load role once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/user/role/", { headers: authHeaders() });
        setRole(data?.role || "staff");
      } catch {
        setRole("staff");
      }
    })();
  }, []);

  // Pull from server
  const params = useMemo(() => {
    const p = {};
    if (start) p.from = start;
    if (end) p.to = end;
    p.order_by = "date_desc";
    return p;
  }, [start, end]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/interviews/", { params, headers: authHeaders() });
      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    if (role && (role === "admin" || role === "staff")) load();
  }, [load, role]);

  // Client-side filters
  const filtered = useMemo(() => {
    const ql = dq.trim().toLowerCase();

    return rows.filter((r) => {
      const hay = [
        r.user_name,
        r.interviewer_name,
      ].map((x) => String(x || "").toLowerCase()).join(" ");

      if (ql && !hay.includes(ql)) return false;

      if (!matchYesNo(inHouseFilter, r.in_house)) return false;
      if (!matchYesNo(onPhoneFilter, r.on_phone)) return false;
      if (!matchYesNo(didNotArriveFilter, r.did_not_arrive)) return false;
      if (!matchYesNo(flexFilter, r.flex)) return false;
      if (!matchYesNo(peerSupportFilter, r.peer_support)) return false;
      if (!matchYesNo(rehabPlanningFilter, r.rehab_planning)) return false;
      if (!matchYesNo(rehabInterviewFilter, r.rehab_interview)) return false;
      if (!matchYesNo(urgentInterviewFilter, r.urgent_interview)) return false;
      if (!matchYesNo(familyInterviewFilter, r.family_interview)) return false;
      if (!matchYesNo(visitFilter, r.visit)) return false;
      if (!matchYesNo(handleidslaFilter, r.handleidsla)) return false;
      if (!matchYesNo(handleidslaNemarFilter, r.handleidsla_nemar)) return false;
      if (!matchYesNo(ororkaFilter, r.ororka)) return false;

      return true;
    });
  }, [
    rows,
    dq,
    inHouseFilter,
    onPhoneFilter,
    didNotArriveFilter,
    flexFilter,
    peerSupportFilter,
    rehabPlanningFilter,
    rehabInterviewFilter,
    urgentInterviewFilter,
    familyInterviewFilter,
    visitFilter,
    handleidslaFilter,
    handleidslaNemarFilter,
    ororkaFilter,
  ]);

  // Reset paging when filters change
  useEffect(() => {
    setOffset(0);
  }, [
    dq,
    start,
    end,
    limit,
    inHouseFilter,
    onPhoneFilter,
    didNotArriveFilter,
    flexFilter,
    peerSupportFilter,
    rehabPlanningFilter,
    rehabInterviewFilter,
    urgentInterviewFilter,
    familyInterviewFilter,
    visitFilter,
    handleidslaFilter,
    handleidslaNemarFilter,
    ororkaFilter,
  ]);

  const total = filtered.length;
  const page = filtered.slice(offset, offset + limit);

  const onPrev = () => setOffset((o) => Math.max(0, o - limit));
  const onNext = () => setOffset((o) => (o + limit < total ? o + limit : o));

  const canView = role === "admin" || role === "staff";
  if (role && !canView) {
    return (
      <Shell title="Hugarafl starfsfólk" rightMenu menuItems={tiles}>
        <h2 style={{ textAlign: "center", marginTop: 20 }}>Aðgangur óheimill</h2>
        <div style={{ textAlign: "center", marginTop: 8, opacity: 0.8 }}>
          Þessi síða er aðeins aðgengileg starfsfólki og stjórnendum.
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="Hugarafl starfsfólk" rightMenu menuItems={tiles}>
      <h2 style={{ textAlign: "center", margin: "8px 0 18px" }}>Viðtöl</h2>

      <div className="members-toolbar" style={{ justifyContent: "space-between" }}>
        {/* Left: search */}
        <div className="search-block">
          <label className="sr" htmlFor="search">Leita</label>
          <input
            id="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Leita (viðmælandi eða viðtalandi)"
            style={{ width: 260 }}
          />
        </div>

        {/* Center: total counter */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              border: "2px solid #222",
              borderRadius: 6,
              padding: "6px 12px",
              textAlign: "center",
              minWidth: 160,
              fontWeight: 600,
            }}
          >
            Viðtöl samtals
            <div style={{ fontSize: 18 }}>{total}</div>
          </div>
        </div>

        {/* Right: filter dropdown */}
        <div className="filter-block">
          <button className="btn sm" onClick={() => setFiltersOpen((v) => !v)}>
            Sía
          </button>

          {filtersOpen && (
            <div className="filter-popover" style={{ minWidth: 340 }}>
              <div className="filter-title">Sía eftir dagsetningum:</div>

              <div className="filter-row">
                <label>Frá:</label>
                <MiniDatePickerDMY value={start} onChange={setStart} />
              </div>

              <div className="filter-row">
                <label>Til:</label>
                <MiniDatePickerDMY value={end} onChange={setEnd} />
              </div>

              <div className="filter-title" style={{ marginTop: 10 }}>Já/Nei síur:</div>

              <div className="filter-row">
                <label>Kom í hús:</label>
                <select value={inHouseFilter} onChange={(e) => setInHouseFilter(e.target.value)}>
                  <option value="">Allt</option>
                  <option value="yes">Já</option>
                  <option value="no">Nei</option>
                </select>
              </div>

              <div className="filter-row">
                <label>Símaviðtal:</label>
                <select value={onPhoneFilter} onChange={(e) => setOnPhoneFilter(e.target.value)}>
                  <option value="">Allt</option>
                  <option value="yes">Já</option>
                  <option value="no">Nei</option>
                </select>
              </div>

              <div className="filter-row">
                <label>Mætti ekki:</label>
                <select value={didNotArriveFilter} onChange={(e) => setDidNotArriveFilter(e.target.value)}>
                  <option value="">Allt</option>
                  <option value="yes">Já</option>
                  <option value="no">Nei</option>
                </select>
              </div>

              <div className="filter-row">
                <label>Flex:</label>
                <select value={flexFilter} onChange={(e) => setFlexFilter(e.target.value)}>
                  <option value="">Allt</option>
                  <option value="yes">Já</option>
                  <option value="no">Nei</option>
                </select>
              </div>

              <div className="filter-row">
                <label>Jafningjastuðningur:</label>
                <select value={peerSupportFilter} onChange={(e) => setPeerSupportFilter(e.target.value)}>
                  <option value="">Allt</option>
                  <option value="yes">Já</option>
                  <option value="no">Nei</option>
                </select>
              </div>

              <div className="filter-row">
                <label>Endurh. áætlun:</label>
                <select value={rehabPlanningFilter} onChange={(e) => setRehabPlanningFilter(e.target.value)}>
                  <option value="">Allt</option>
                  <option value="yes">Já</option>
                  <option value="no">Nei</option>
                </select>
              </div>

              {/* ✅ NEW */}
              <div className="filter-row">
                <label>Endurhæfingarviðtal:</label>
                <select value={rehabInterviewFilter} onChange={(e) => setRehabInterviewFilter(e.target.value)}>
                  <option value="">Allt</option>
                  <option value="yes">Já</option>
                  <option value="no">Nei</option>
                </select>
              </div>

              <div className="filter-row">
                <label>Bráðaviðtal:</label>
                <select value={urgentInterviewFilter} onChange={(e) => setUrgentInterviewFilter(e.target.value)}>
                  <option value="">Allt</option>
                  <option value="yes">Já</option>
                  <option value="no">Nei</option>
                </select>
              </div>

              <div className="filter-row">
                <label>Fjölskylduviðtal:</label>
                <select value={familyInterviewFilter} onChange={(e) => setFamilyInterviewFilter(e.target.value)}>
                  <option value="">Allt</option>
                  <option value="yes">Já</option>
                  <option value="no">Nei</option>
                </select>
              </div>

              <div className="filter-row">
                <label>Vitjun:</label>
                <select value={visitFilter} onChange={(e) => setVisitFilter(e.target.value)}>
                  <option value="">Allt</option>
                  <option value="yes">Já</option>
                  <option value="no">Nei</option>
                </select>
              </div>

              <div className="filter-row">
                <label>Handleiðsla:</label>
                <select value={handleidslaFilter} onChange={(e) => setHandleidslaFilter(e.target.value)}>
                  <option value="">Allt</option>
                  <option value="yes">Já</option>
                  <option value="no">Nei</option>
                </select>
              </div>

              <div className="filter-row">
                <label>Handleiðsla nemar:</label>
                <select value={handleidslaNemarFilter} onChange={(e) => setHandleidslaNemarFilter(e.target.value)}>
                  <option value="">Allt</option>
                  <option value="yes">Já</option>
                  <option value="no">Nei</option>
                </select>
              </div>

              <div className="filter-row">
                <label>Ororka:</label>
                <select value={ororkaFilter} onChange={(e) => setOrorkaFilter(e.target.value)}>
                  <option value="">Allt</option>
                  <option value="yes">Já</option>
                  <option value="no">Nei</option>
                </select>
              </div>

              <div className="filter-title" style={{ marginTop: 10 }}>Fjöldi á síðu:</div>
              <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))}>
                {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
                <button
                  className="btn ghost sm"
                  onClick={() => {
                    setQ("");
                    setStart(dateToIso(new Date()));
                    setEnd(dateToIso(new Date()));
                    setLimit(100);
                    setInHouseFilter("");
                    setOnPhoneFilter("");
                    setDidNotArriveFilter("");
                    setFlexFilter("");
                    setPeerSupportFilter("");
                    setRehabPlanningFilter("");
                    setRehabInterviewFilter("");
                    setUrgentInterviewFilter("");
                    setFamilyInterviewFilter("");
                    setVisitFilter("");
                    setHandleidslaFilter("");
                    setHandleidslaNemarFilter("");
                    setOrorkaFilter("");
                  }}
                >
                  Hreinsa
                </button>
                <button className="btn ghost sm" onClick={() => setFiltersOpen(false)}>
                  Loka síu
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading">Hleð…</div>
      ) : (
        <>
          <div className="table-wrap">
            <table className="table zebra">
              <thead>
                <tr>
                  <th>Viðmælandi</th>
                  <th>Viðtalandi</th>
                  <th>Kom í hús</th>
                  <th>Síma viðtal</th>
                  <th>Mætti ekki</th>
                  <th>Flex</th>
                  <th>Jafningjastuðningur</th>
                  <th>Endurh. áætlun</th>
                  <th>Endurhæfingarviðtal</th>
                  <th>Bráðaviðtal</th>
                  <th>Fjölskylduviðtal</th>
                  <th>Vitjun</th>
                  <th>Handleiðsla</th>
                  <th>Handleiðsla nemar</th>
                  <th>Ororka</th>
                  <th>Dagsetning</th>
                </tr>
              </thead>
              <tbody>
                {page.length === 0 ? (
                  <tr>
                    <td colSpan={15} style={{ textAlign: "center", padding: 16 }}>
                      Engin viðtöl fundust fyrir valið tímabil
                    </td>
                  </tr>
                ) : (
                  page.map((r) => (
                    <tr key={r.id}>
                      <td>{r.user_name || "—"}</td>
                      <td>{r.interviewer_name || "—"}</td>
                      <td>{r.in_house ? "Já" : "Nei"}</td>
                      <td>{r.on_phone ? "Já" : "Nei"}</td>
                      <td>{r.did_not_arrive ? "Já" : "Nei"}</td>
                      <td>{r.flex ? "Já" : "Nei"}</td>
                      <td>{r.peer_support ? "Já" : "Nei"}</td>
                      <td>{r.rehab_planning ? "Já" : "Nei"}</td>
                      <td>{r.rehab_interview ? "Já" : "Nei"}</td>
                      <td>{r.urgent_interview ? "Já" : "Nei"}</td>
                      <td>{r.family_interview ? "Já" : "Nei"}</td>
                      <td>{r.visit ? "Já" : "Nei"}</td>
                      <td>{r.handleidsla ? "Já" : "Nei"}</td>
                      <td>{r.handleidsla_nemar ? "Já" : "Nei"}</td>
                      <td>{r.ororka ? "Já" : "Nei"}</td>
                      <td>{fmtDate(r.interview_date)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
            <div style={{ opacity: 0.8 }}>
              Sýni {page.length} af {total}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn sm ghost" onClick={onPrev} disabled={offset === 0}>Fyrri</button>
              <button className="btn sm" onClick={onNext} disabled={offset + limit >= total}>Næsta</button>
            </div>
          </div>
        </>
      )}
    </Shell>
  );
}